import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Mail, Plus, Edit2, Trash2, RefreshCw, Play, Pause,
  CheckCircle2, XCircle, AlertCircle, Clock, Settings,
  Inbox, Filter, FileText, Activity, TestTube, Zap
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { formatDate } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// API Functions
async function listEmailConfigs() {
  const response = await fetch(`${API_URL}/api/ingestion/email-configs`);
  if (!response.ok) throw new Error('Failed to fetch configs');
  return response.json();
}

async function getIngestionStats() {
  const response = await fetch(`${API_URL}/api/ingestion/stats`);
  if (!response.ok) throw new Error('Failed to fetch stats');
  return response.json();
}

async function createEmailConfig(data) {
  const response = await fetch(`${API_URL}/api/ingestion/email-configs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to create config');
  return response.json();
}

async function updateEmailConfig(id, data) {
  const response = await fetch(`${API_URL}/api/ingestion/email-configs/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) throw new Error('Failed to update config');
  return response.json();
}

async function deleteEmailConfig(id) {
  const response = await fetch(`${API_URL}/api/ingestion/email-configs/${id}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Failed to delete config');
  return response.json();
}

async function testEmailConfig(id) {
  const response = await fetch(`${API_URL}/api/ingestion/email-configs/${id}/test`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to test config');
  return response.json();
}

async function startMonitoring(id) {
  const response = await fetch(`${API_URL}/api/ingestion/email-configs/${id}/start`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to start monitoring');
  return response.json();
}

async function stopMonitoring(id) {
  const response = await fetch(`${API_URL}/api/ingestion/email-configs/${id}/stop`, {
    method: 'POST'
  });
  if (!response.ok) throw new Error('Failed to stop monitoring');
  return response.json();
}

async function listEmailLogs(configId) {
  const url = configId 
    ? `${API_URL}/api/ingestion/email-logs?config_id=${configId}`
    : `${API_URL}/api/ingestion/email-logs`;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch logs');
  return response.json();
}

// Status Badge Component
function StatusBadge({ status }) {
  const statusConfig = {
    'ACTIVE': { color: 'bg-green-100 text-green-800', icon: CheckCircle2, text: 'Active' },
    'INACTIVE': { color: 'bg-gray-100 text-gray-800', icon: Pause, text: 'Inactive' },
    'ERROR': { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Error' },
    'TESTING': { color: 'bg-blue-100 text-blue-800', icon: Clock, text: 'Testing' }
  };
  
  const config = statusConfig[status] || statusConfig['INACTIVE'];
  const Icon = config.icon;
  
  return (
    <Badge className={config.color}>
      <Icon className="w-3 h-3 mr-1" />
      {config.text}
    </Badge>
  );
}

// Email Config Form
function EmailConfigForm({ config, onSave, onCancel }) {
  const [formData, setFormData] = useState(config || {
    name: '',
    provider: 'IMAP',
    mailbox: '',
    folder: 'INBOX',
    credentials: {
      host: '',
      port: 993,
      username: '',
      password: ''
    },
    filter_rules: {
      from_addresses: [],
      subject_contains: ['EOB', 'Remittance', 'Payment', 'Explanation of Benefits'],
      has_attachment: true
    },
    attachment_rules: {
      allowed_types: ['pdf', 'tiff', 'png'],
      max_size_mb: 25,
      naming_pattern: ''
    },
    processing_options: {
      auto_process: true,
      mark_as_read: true,
      move_to_folder: 'Processed',
      delete_after_days: 0
    },
    polling_interval_minutes: 5,
    is_active: true
  });

  const [fromAddressesInput, setFromAddressesInput] = useState(
    formData.filter_rules?.from_addresses?.join(', ') || ''
  );
  const [subjectInput, setSubjectInput] = useState(
    formData.filter_rules?.subject_contains?.join(', ') || 'EOB, Remittance, Payment'
  );

  const handleSubmit = () => {
    const data = {
      ...formData,
      filter_rules: {
        ...formData.filter_rules,
        from_addresses: fromAddressesInput.split(',').map(s => s.trim()).filter(Boolean),
        subject_contains: subjectInput.split(',').map(s => s.trim()).filter(Boolean)
      }
    };
    onSave(data);
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Configuration Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Payer EOBs - Main"
          />
        </div>
        <div className="space-y-2">
          <Label>Email Provider *</Label>
          <Select
            value={formData.provider}
            onValueChange={(value) => setFormData({ ...formData, provider: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="IMAP">IMAP/IMAPS</SelectItem>
              <SelectItem value="M365">Microsoft 365</SelectItem>
              <SelectItem value="GOOGLE">Google Workspace</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Email Address to Monitor *</Label>
          <Input
            value={formData.mailbox}
            onChange={(e) => setFormData({ ...formData, mailbox: e.target.value })}
            placeholder="eob-inbox@yourcompany.com"
          />
        </div>
        <div className="space-y-2">
          <Label>Folder to Monitor</Label>
          <Input
            value={formData.folder}
            onChange={(e) => setFormData({ ...formData, folder: e.target.value })}
            placeholder="INBOX"
          />
        </div>
      </div>

      {/* Credentials - IMAP */}
      {formData.provider === 'IMAP' && (
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-medium flex items-center">
            <Settings className="w-4 h-4 mr-2" />
            IMAP Connection Settings
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>IMAP Server Host</Label>
              <Input
                value={formData.credentials?.host || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  credentials: { ...formData.credentials, host: e.target.value }
                })}
                placeholder="imap.gmail.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Port</Label>
              <Input
                type="number"
                value={formData.credentials?.port || 993}
                onChange={(e) => setFormData({
                  ...formData,
                  credentials: { ...formData.credentials, port: parseInt(e.target.value) }
                })}
              />
            </div>
            <div className="space-y-2">
              <Label>Username</Label>
              <Input
                value={formData.credentials?.username || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  credentials: { ...formData.credentials, username: e.target.value }
                })}
                placeholder="username@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password / App Password</Label>
              <Input
                type="password"
                value={formData.credentials?.password || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  credentials: { ...formData.credentials, password: e.target.value }
                })}
                placeholder="••••••••"
              />
            </div>
          </div>
        </div>
      )}

      {/* Credentials - M365 */}
      {formData.provider === 'M365' && (
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-medium flex items-center">
            <Settings className="w-4 h-4 mr-2" />
            Microsoft 365 OAuth Settings
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tenant ID</Label>
              <Input
                value={formData.credentials?.tenant_id || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  credentials: { ...formData.credentials, tenant_id: e.target.value }
                })}
                placeholder="your-tenant-id"
              />
            </div>
            <div className="space-y-2">
              <Label>Client ID</Label>
              <Input
                value={formData.credentials?.client_id || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  credentials: { ...formData.credentials, client_id: e.target.value }
                })}
                placeholder="your-client-id"
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Client Secret</Label>
              <Input
                type="password"
                value={formData.credentials?.client_secret || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  credentials: { ...formData.credentials, client_secret: e.target.value }
                })}
                placeholder="••••••••"
              />
            </div>
          </div>
        </div>
      )}

      {/* Filter Rules */}
      <div className="border rounded-lg p-4 space-y-4">
        <h4 className="font-medium flex items-center">
          <Filter className="w-4 h-4 mr-2" />
          Email Filter Rules
        </h4>
        <div className="space-y-2">
          <Label>From Addresses/Domains (comma-separated)</Label>
          <Input
            value={fromAddressesInput}
            onChange={(e) => setFromAddressesInput(e.target.value)}
            placeholder="@anthem.com, @bcbs.com, @unitedhealthcare.com"
          />
          <p className="text-xs text-muted-foreground">Leave empty to accept from all senders</p>
        </div>
        <div className="space-y-2">
          <Label>Subject Must Contain (comma-separated)</Label>
          <Input
            value={subjectInput}
            onChange={(e) => setSubjectInput(e.target.value)}
            placeholder="EOB, Remittance, Payment"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            checked={formData.filter_rules?.has_attachment}
            onCheckedChange={(checked) => setFormData({
              ...formData,
              filter_rules: { ...formData.filter_rules, has_attachment: checked }
            })}
          />
          <Label>Must have attachment</Label>
        </div>
      </div>

      {/* Processing Options */}
      <div className="border rounded-lg p-4 space-y-4">
        <h4 className="font-medium flex items-center">
          <Zap className="w-4 h-4 mr-2" />
          Processing Options
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Polling Interval (minutes)</Label>
            <Input
              type="number"
              value={formData.polling_interval_minutes}
              onChange={(e) => setFormData({ ...formData, polling_interval_minutes: parseInt(e.target.value) })}
              min={1}
              max={60}
            />
          </div>
          <div className="space-y-2">
            <Label>Move Processed to Folder</Label>
            <Input
              value={formData.processing_options?.move_to_folder || ''}
              onChange={(e) => setFormData({
                ...formData,
                processing_options: { ...formData.processing_options, move_to_folder: e.target.value }
              })}
              placeholder="Processed"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.processing_options?.auto_process}
              onCheckedChange={(checked) => setFormData({
                ...formData,
                processing_options: { ...formData.processing_options, auto_process: checked }
              })}
            />
            <Label>Auto-process attachments</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.processing_options?.mark_as_read}
              onCheckedChange={(checked) => setFormData({
                ...formData,
                processing_options: { ...formData.processing_options, mark_as_read: checked }
              })}
            />
            <Label>Mark as read</Label>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={!formData.name || !formData.mailbox}>
          {config ? 'Update' : 'Create'} Configuration
        </Button>
      </div>
    </div>
  );
}

// Stats Cards
function StatsCards({ stats }) {
  if (!stats) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs flex items-center">
            <Mail className="w-3 h-3 mr-1" />Active Connections
          </CardDescription>
          <CardTitle className="text-3xl">
            {stats.configs?.active || 0} / {stats.configs?.total || 0}
          </CardTitle>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs flex items-center">
            <Inbox className="w-3 h-3 mr-1" />Emails Processed
          </CardDescription>
          <CardTitle className="text-3xl">{stats.totals?.emails_processed || 0}</CardTitle>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs flex items-center">
            <FileText className="w-3 h-3 mr-1" />Attachments Extracted
          </CardDescription>
          <CardTitle className="text-3xl">{stats.totals?.attachments_extracted || 0}</CardTitle>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardDescription className="text-xs flex items-center">
            <AlertCircle className="w-3 h-3 mr-1" />Queue Status
          </CardDescription>
          <CardTitle className="text-xl">
            <span className="text-muted-foreground">{stats.queue?.pending || 0} pending</span>
            {stats.queue?.failed > 0 && (
              <span className="text-red-500 ml-2">{stats.queue.failed} failed</span>
            )}
          </CardTitle>
        </CardHeader>
      </Card>
    </div>
  );
}

// Activity Log
function ActivityLog({ logs }) {
  if (!logs || logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No recent activity</p>
      </div>
    );
  }

  const statusColors = {
    'COMPLETED': 'text-green-600',
    'FAILED': 'text-red-600',
    'PENDING': 'text-yellow-600',
    'PROCESSING': 'text-blue-600',
    'DUPLICATE': 'text-gray-600'
  };

  return (
    <div className="space-y-2">
      {logs.map((log, idx) => (
        <div key={idx} className="flex items-start justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex-1">
            <p className="text-sm font-medium truncate max-w-md">{log.subject}</p>
            <p className="text-xs text-muted-foreground">
              From: {log.sender} • {formatDate(log.created_at)}
            </p>
          </div>
          <div className="text-right">
            <span className={`text-xs font-medium ${statusColors[log.status] || 'text-gray-600'}`}>
              {log.status}
            </span>
            {log.attachments_processed > 0 && (
              <p className="text-xs text-muted-foreground">
                {log.attachments_processed} files
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Main Page Component
export default function EmailIngestionPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  const { data: configsData, isLoading } = useQuery({
    queryKey: ['email-configs'],
    queryFn: listEmailConfigs,
  });

  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['ingestion-stats'],
    queryFn: getIngestionStats,
    refetchInterval: 10000,
  });

  const { data: logsData } = useQuery({
    queryKey: ['email-logs'],
    queryFn: () => listEmailLogs(),
    refetchInterval: 10000,
  });

  const createMutation = useMutation({
    mutationFn: createEmailConfig,
    onSuccess: () => {
      toast.success('Email configuration created!');
      queryClient.invalidateQueries(['email-configs']);
      setShowForm(false);
    },
    onError: (error) => toast.error(`Failed: ${error.message}`)
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateEmailConfig(id, data),
    onSuccess: () => {
      toast.success('Configuration updated!');
      queryClient.invalidateQueries(['email-configs']);
      setEditingConfig(null);
    },
    onError: (error) => toast.error(`Failed: ${error.message}`)
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmailConfig,
    onSuccess: () => {
      toast.success('Configuration deleted!');
      queryClient.invalidateQueries(['email-configs']);
    },
    onError: (error) => toast.error(`Failed: ${error.message}`)
  });

  const testMutation = useMutation({
    mutationFn: testEmailConfig,
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
      queryClient.invalidateQueries(['email-configs']);
    },
    onError: (error) => toast.error(`Test failed: ${error.message}`)
  });

  const startMutation = useMutation({
    mutationFn: startMonitoring,
    onSuccess: () => {
      toast.success('Monitoring started!');
      queryClient.invalidateQueries(['email-configs', 'ingestion-stats']);
    },
    onError: (error) => toast.error(`Failed: ${error.message}`)
  });

  const stopMutation = useMutation({
    mutationFn: stopMonitoring,
    onSuccess: () => {
      toast.success('Monitoring stopped!');
      queryClient.invalidateQueries(['email-configs', 'ingestion-stats']);
    },
    onError: (error) => toast.error(`Failed: ${error.message}`)
  });

  const handleSave = (data) => {
    if (editingConfig) {
      updateMutation.mutate({ id: editingConfig.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const configs = configsData?.configs || [];
  const logs = logsData?.logs || stats?.recent_activity || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-ingestion-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <Mail className="w-6 h-6 mr-2" />
            Email Ingestion
          </h2>
          <p className="text-sm text-muted-foreground">Automatically process EOB attachments from email</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={() => refetchStats()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => { setEditingConfig(null); setShowForm(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Connection
          </Button>
        </div>
      </div>

      {/* Stats */}
      <StatsCards stats={stats} />

      {/* Form Dialog */}
      <Dialog open={showForm || !!editingConfig} onOpenChange={(open) => {
        if (!open) {
          setShowForm(false);
          setEditingConfig(null);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingConfig ? 'Edit Email Configuration' : 'Add Email Connection'}
            </DialogTitle>
            <DialogDescription>
              Configure an email inbox to monitor for EOB attachments
            </DialogDescription>
          </DialogHeader>
          <EmailConfigForm
            config={editingConfig}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingConfig(null); }}
          />
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configurations */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Email Connections</CardTitle>
              <CardDescription>Configured email inboxes for EOB ingestion</CardDescription>
            </CardHeader>
            <CardContent>
              {configs.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No email connections configured</p>
                  <Button onClick={() => setShowForm(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Connection
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {configs.map((config) => (
                    <div key={config.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="font-medium">{config.name}</h4>
                            <StatusBadge status={config.status} />
                            <Badge variant="outline">{config.provider}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{config.mailbox}</p>
                          <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                            <span>Folder: {config.folder}</span>
                            <span>Poll: {config.polling_interval_minutes}m</span>
                            <span>Processed: {config.emails_processed || 0} emails</span>
                          </div>
                          {config.last_error && (
                            <p className="text-xs text-red-500 mt-1">Error: {config.last_error}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => testMutation.mutate(config.id)}
                            disabled={testMutation.isPending}
                          >
                            <TestTube className="w-4 h-4" />
                          </Button>
                          {config.status === 'ACTIVE' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => stopMutation.mutate(config.id)}
                            >
                              <Pause className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => startMutation.mutate(config.id)}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingConfig(config)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteMutation.mutate(config.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Activity Log */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center">
                <Activity className="w-4 h-4 mr-2" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ActivityLog logs={logs} />
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="mt-4 border-primary/50 bg-primary/5">
            <CardContent className="pt-6">
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold mb-1">Email Ingestion</h4>
                  <p className="text-xs text-muted-foreground">
                    CLAIRVOY monitors configured email inboxes for EOB attachments.
                    When detected, files are automatically extracted and queued for AI processing.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
